import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

const APIEndpoints = () => {
  const [selectedCategory, setSelectedCategory] = useState('policies');
  const [expandedEndpoint, setExpandedEndpoint] = useState(null);

  const categories = [
    { id: 'policies', label: 'Policies', icon: 'FileText', count: 12 },
    { id: 'payers', label: 'Payers', icon: 'Building2', count: 8 },
    { id: 'webhooks', label: 'Webhooks', icon: 'Webhook', count: 6 },
    { id: 'analytics', label: 'Analytics', icon: 'BarChart3', count: 10 },
    { id: 'search', label: 'Search', icon: 'Search', count: 5 }
  ];

  const endpoints = {
    policies: [
      {
        id: 'list-policies',
        method: 'GET',
        path: '/v2/policies',
        title: 'List Policies',
        description: 'Retrieve a paginated list of medical device policies with filtering options.',
        parameters: [
          { name: 'category', type: 'string', required: false, description: 'Filter by device category (cardiovascular, orthopedic, etc.)' },
          { name: 'status', type: 'string', required: false, description: 'Filter by policy status (active, archived, pending)' },
          { name: 'payer', type: 'string', required: false, description: 'Filter by insurance payer name' },
          { name: 'limit', type: 'integer', required: false, description: 'Number of results per page (default: 20, max: 100)' },
          { name: 'offset', type: 'integer', required: false, description: 'Pagination offset' }
        ],
        response: `{
  "data": [
    {
      "id": "pol_abc123",
      "title": "Cardiovascular Device Coverage Policy",
      "payer": "Anthem Blue Cross",
      "category": "cardiovascular",
      "status": "active",
      "effective_date": "2026-01-15",
      "last_updated": "2026-01-20T10:30:00Z",
      "coverage_criteria": "...",
      "documentation_requirements": ["..."]
    }
  ],
  "pagination": {
    "total": 1247,
    "limit": 20,
    "offset": 0,
    "has_more": true
  }
}`
      },
      {
        id: 'get-policy',
        method: 'GET',
        path: '/v2/policies/{policy_id}',
        title: 'Get Policy Details',
        description: 'Retrieve detailed information about a specific policy including full coverage criteria and change history.',
        parameters: [
          { name: 'policy_id', type: 'string', required: true, description: 'Unique policy identifier' }
        ],
        response: `{
  "id": "pol_abc123",
  "title": "Cardiovascular Device Coverage Policy",
  "payer": "Anthem Blue Cross",
  "category": "cardiovascular",
  "status": "active",
  "effective_date": "2026-01-15",
  "last_updated": "2026-01-20T10:30:00Z",
  "coverage_criteria": "Detailed coverage requirements...",
  "documentation_requirements": ["Prior authorization", "Clinical notes"],
  "change_history": [
    {
      "date": "2026-01-20",
      "type": "criteria_update",
      "description": "Updated age requirements"
    }
  ]
}`
      },
      {
        id: 'policy-changes',
        method: 'GET',
        path: '/v2/policies/changes',
        title: 'Policy Change Feed',
        description: 'Real-time feed of policy updates and modifications across all monitored payers.',
        parameters: [
          { name: 'since', type: 'datetime', required: false, description: 'Return changes after this timestamp' },
          { name: 'category', type: 'string', required: false, description: 'Filter by device category' }
        ],
        response: `{
  "changes": [
    {
      "policy_id": "pol_abc123",
      "change_type": "criteria_update",
      "timestamp": "2026-01-20T10:30:00Z",
      "summary": "Updated coverage criteria for cardiovascular devices",
      "impact_level": "high"
    }
  ]
}`
      }
    ],
    payers: [
      {
        id: 'list-payers',
        method: 'GET',
        path: '/v2/payers',
        title: 'List Payers',
        description: 'Get a list of all insurance payers monitored by PolicyAlerts.',
        parameters: [
          { name: 'state', type: 'string', required: false, description: 'Filter by US state code' },
          { name: 'type', type: 'string', required: false, description: 'Filter by payer type (commercial, medicare, medicaid)' }
        ],
        response: `{
  "data": [
    {
      "id": "payer_123",
      "name": "Anthem Blue Cross",
      "type": "commercial",
      "states": ["CA", "NY", "TX"],
      "policy_count": 247
    }
  ]
}`
      }
    ],
    webhooks: [
      {
        id: 'create-webhook',
        method: 'POST',
        path: '/v2/webhooks',
        title: 'Create Webhook',
        description: 'Register a webhook endpoint to receive real-time policy change notifications.',
        parameters: [
          { name: 'url', type: 'string', required: true, description: 'Your webhook endpoint URL' },
          { name: 'events', type: 'array', required: true, description: 'Array of event types to subscribe to' },
          { name: 'filters', type: 'object', required: false, description: 'Optional filters for webhook events' }
        ],
        response: `{
  "id": "wh_abc123",
  "url": "https://your-app.com/webhooks/policy-alerts",
  "events": ["policy.updated", "policy.created"],
  "status": "active",
  "created_at": "2026-01-20T10:30:00Z"
}`
      }
    ],
    analytics: [
      {
        id: 'policy-trends',
        method: 'GET',
        path: '/v2/analytics/trends',
        title: 'Policy Trends',
        description: 'Analyze policy change trends over time with aggregated statistics.',
        parameters: [
          { name: 'start_date', type: 'date', required: true, description: 'Start date for analysis' },
          { name: 'end_date', type: 'date', required: true, description: 'End date for analysis' },
          { name: 'category', type: 'string', required: false, description: 'Filter by device category' }
        ],
        response: `{
  "period": {
    "start": "2026-01-01",
    "end": "2026-01-28"
  },
  "total_changes": 342,
  "by_category": {
    "cardiovascular": 127,
    "orthopedic": 98
  }
}`
      }
    ],
    search: [
      {
        id: 'search-policies',
        method: 'POST',
        path: '/v2/search',
        title: 'Search Policies',
        description: 'Advanced full-text search across policy content with relevance scoring.',
        parameters: [
          { name: 'query', type: 'string', required: true, description: 'Search query string' },
          { name: 'filters', type: 'object', required: false, description: 'Additional search filters' }
        ],
        response: `{
  "results": [
    {
      "policy_id": "pol_abc123",
      "title": "Cardiovascular Device Coverage",
      "relevance_score": 0.95,
      "matched_terms": ["cardiovascular", "coverage"]
    }
  ]
}`
      }
    ]
  };

  const toggleEndpoint = (endpointId) => {
    setExpandedEndpoint(expandedEndpoint === endpointId ? null : endpointId);
  };

  const getMethodColor = (method) => {
    const colors = {
      GET: 'bg-green-100 text-green-700 border-green-300',
      POST: 'bg-blue-100 text-blue-700 border-blue-300',
      PUT: 'bg-yellow-100 text-yellow-700 border-yellow-300',
      DELETE: 'bg-red-100 text-red-700 border-red-300'
    };
    return colors?.[method] || 'bg-gray-100 text-gray-700 border-gray-300';
  };

  return (
    <section className="py-12 md:py-16 lg:py-20 px-4 md:px-6 lg:px-8 bg-gray-50">
      <div className="max-w-7xl mx-auto">
        <div className="text-center mb-12">
          <h2 className="text-2xl md:text-3xl lg:text-4xl font-bold text-gray-900 mb-4">
            API Reference
          </h2>
          <p className="text-base md:text-lg text-gray-600 max-w-2xl mx-auto">
            Complete documentation for all PolicyAlerts API endpoints with request/response examples.
          </p>
        </div>

        <div className="flex flex-col lg:flex-row gap-6 lg:gap-8">
          <div className="lg:w-64 flex-shrink-0">
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4 lg:sticky lg:top-24">
              <h3 className="text-sm font-semibold text-gray-900 mb-4">Categories</h3>
              <div className="space-y-2">
                {categories?.map((category) => (
                  <button
                    key={category?.id}
                    onClick={() => setSelectedCategory(category?.id)}
                    className={`w-full flex items-center justify-between px-3 py-2 rounded-lg text-sm font-medium transition-colors ${
                      selectedCategory === category?.id
                        ? 'bg-blue-50 text-blue-700' :'text-gray-600 hover:bg-gray-50'
                    }`}
                  >
                    <div className="flex items-center gap-2">
                      <Icon name={category?.icon} size={16} />
                      <span>{category?.label}</span>
                    </div>
                    <span className={`text-xs px-2 py-0.5 rounded-full ${
                      selectedCategory === category?.id
                        ? 'bg-blue-100 text-blue-700' :'bg-gray-100 text-gray-600'
                    }`}>
                      {category?.count}
                    </span>
                  </button>
                ))}
              </div>
            </div>
          </div>

          <div className="flex-1 space-y-4">
            {endpoints?.[selectedCategory]?.map((endpoint) => (
              <div key={endpoint?.id} className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
                <button
                  onClick={() => toggleEndpoint(endpoint?.id)}
                  className="w-full px-4 md:px-6 py-4 flex items-center justify-between hover:bg-gray-50 transition-colors"
                >
                  <div className="flex items-center gap-3 md:gap-4 flex-1 min-w-0">
                    <span className={`px-2 md:px-3 py-1 rounded text-xs font-semibold border ${getMethodColor(endpoint?.method)}`}>
                      {endpoint?.method}
                    </span>
                    <div className="flex-1 min-w-0 text-left">
                      <h4 className="text-sm md:text-base font-semibold text-gray-900 mb-1">
                        {endpoint?.title}
                      </h4>
                      <code className="text-xs md:text-sm text-gray-600 break-all">
                        {endpoint?.path}
                      </code>
                    </div>
                  </div>
                  <Icon 
                    name={expandedEndpoint === endpoint?.id ? 'ChevronUp' : 'ChevronDown'} 
                    size={20} 
                    className="flex-shrink-0"
                  />
                </button>

                {expandedEndpoint === endpoint?.id && (
                  <div className="px-4 md:px-6 pb-6 border-t border-gray-200">
                    <p className="text-sm md:text-base text-gray-600 mb-6 mt-4">
                      {endpoint?.description}
                    </p>

                    <div className="mb-6">
                      <h5 className="text-sm font-semibold text-gray-900 mb-3">Parameters</h5>
                      <div className="space-y-3">
                        {endpoint?.parameters?.map((param, index) => (
                          <div key={index} className="flex flex-col sm:flex-row sm:items-start gap-2 sm:gap-4 p-3 bg-gray-50 rounded-lg">
                            <div className="flex items-center gap-2 flex-shrink-0">
                              <code className="text-sm font-mono text-blue-600">{param?.name}</code>
                              {param?.required && (
                                <span className="text-xs px-2 py-0.5 bg-red-100 text-red-700 rounded">Required</span>
                              )}
                            </div>
                            <div className="flex-1 min-w-0">
                              <p className="text-xs text-gray-500 mb-1">{param?.type}</p>
                              <p className="text-sm text-gray-700">{param?.description}</p>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>

                    <div>
                      <div className="flex items-center justify-between mb-3">
                        <h5 className="text-sm font-semibold text-gray-900">Response Example</h5>
                        <Button variant="ghost" size="xs" iconName="Copy">
                          Copy
                        </Button>
                      </div>
                      <div className="bg-gray-900 rounded-lg p-4 overflow-x-auto">
                        <pre className="text-green-400 text-xs md:text-sm">
                          <code>{endpoint?.response}</code>
                        </pre>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};

export default APIEndpoints;